import * as React from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

import { RootState } from '../../reducers';
import { updateSettings, NewSettings } from './settingsSlice';
import { recordingSubSettings, SubSettings } from './common';
import { RecorderSettings } from '../../types/state';
import { formatString } from './utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  SettingsSection,
  SettingsLayout,
} from '../../components/ui/settings-section';
import profiles, { ProfileType } from '../../constants/mediaFormatProfiles';

const RecordingSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const recorder = useSelector((state: RootState) => state.settings.recorder);
  const [modifiedSettings, setModifiedSettings] =
    useState<RecorderSettings>(recorder);
  const [lastSavedSettings, setLastSavedSettings] =
    useState<RecorderSettings>(recorder);

  useEffect(() => {
    setModifiedSettings(recorder);
    setLastSavedSettings(recorder);
  }, [recorder]);

  const handleAutoSave = async () => {
    try {
      await (dispatch(
        updateSettings({
          key: 'recorder',
          newSettings: modifiedSettings as NewSettings,
        }),
      ) as any);

      // Update last saved settings to prevent re-saving
      setLastSavedSettings(modifiedSettings);

      // Show subtle success feedback
      toast(t('settings.recorder.autoSaved', 'Settings saved'), {
        duration: 2000,
      });
    } catch (error) {
      // Show error toast
      toast.error(
        t(
          'settings.recorder.saveErrorDescription',
          'Failed to save settings. Please try again.',
        ),
        { duration: 4000 },
      );
      console.error('Failed to auto-save settings:', error);
    }
  };

  // Auto-save when settings change
  useEffect(() => {
    // Skip initial render and when settings haven't actually changed
    const hasChanged =
      JSON.stringify(modifiedSettings) !== JSON.stringify(lastSavedSettings);
    if (!hasChanged) return undefined;

    // Debounce the save operation
    const timeoutId = setTimeout(() => {
      handleAutoSave();
    }, 500); // Wait 500ms after user stops making changes

    return () => clearTimeout(timeoutId);
  }, [modifiedSettings]);

  // Format options for dropdowns
  const formatOptions = Object.keys(profiles) as ProfileType[];
  const videoFormatOptions = formatOptions.filter(
    (format) => profiles[format].videoCodec !== '',
  );

  // Time options for max recording
  const timeOptions = [
    { value: 5, label: '5 minutes' },
    { value: 10, label: '10 minutes' },
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 60, label: '1 hour' },
    { value: 120, label: '2 hours' },
    { value: 180, label: '3 hours' },
    { value: 240, label: '4 hours' },
    { value: 300, label: '5 hours' },
    { value: 360, label: '6 hours' },
    { value: 0, label: 'No limit' },
  ];

  const handleChange = (
    subSetting: SubSettings,
    setting: string,
    newValue: boolean | string,
  ) => {
    setModifiedSettings((prevSettings) => ({
      ...prevSettings,
      [subSetting]: {
        ...prevSettings[subSetting],
        [setting]: newValue,
      },
    }));
  };

  const getSettingDescription = (
    setting: string,
    isToggle: boolean,
  ): string => {
    // Helper function to format camelCase to readable text
    const formatSettingName = (text: string): string => {
      return (
        text
          // Split camelCase
          .replace(/([A-Z])/g, ' $1')
          // Remove any special characters and extra spaces
          .replace(/[^a-zA-Z0-9 ]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          // Capitalize first letter of each word
          .split(' ')
          .map(
            (word) =>
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
          )
          .join(' ')
      );
    };

    if (isToggle) {
      const toggleDescriptions: { [key: string]: string } = {
        enableAudio: t(
          'settings.recorder.descriptions.enableAudio',
          'Record audio during screen captures',
        ),
        enableVideo: t(
          'settings.recorder.descriptions.enableVideo',
          'Include video in your recordings',
        ),
        startRecordingAuto: t(
          'settings.recorder.descriptions.startRecordingAuto',
          'Automatically start recording when recorder opens',
        ),
        autoDownload: t(
          'settings.recorder.descriptions.autoDownload',
          'Automatically download recordings after completion',
        ),
        notifications: t(
          'settings.recorder.descriptions.notifications',
          'Show notifications during recording sessions',
        ),
        browserAudio: t(
          'settings.recorder.descriptions.browserAudio',
          'Capture audio from browser tabs and web applications',
        ),
        systemAudio: t(
          'settings.recorder.descriptions.systemAudio',
          'Record all system audio output (desktop audio)',
        ),
        microphoneAudio: t(
          'settings.recorder.descriptions.microphoneAudio',
          'Record audio from your microphone',
        ),
        webCam: t(
          'settings.recorder.descriptions.webCam',
          'Include webcam video in your recording',
        ),
        screen: t(
          'settings.recorder.descriptions.screen',
          'Capture your screen content',
        ),
        showCursor: t(
          'settings.recorder.descriptions.showCursor',
          'Display cursor movements in recording',
        ),
        highlightClicks: t(
          'settings.recorder.descriptions.highlightClicks',
          'Highlight mouse clicks in recording',
        ),
        generatePublicUrl: t(
          'settings.recorder.descriptions.generatePublicUrl',
          'Automatically generate public shareable links for new recordings',
        ),
        downloadSharedVideos: t(
          'settings.recorder.descriptions.downloadSharedVideos',
          'Allow viewers to download your shared recordings',
        ),
        hideTitleAndDescriptionDialog: t(
          'settings.recorder.descriptions.hideTitleAndDescriptionDialog',
          'Skip the title and description dialog after recording finishes',
        ),
        webhookUrl: t(
          'settings.recorder.descriptions.webhookUrl',
          'Webhook endpoint to receive recording completion notifications',
        ),
        autoRenameRecordings: t(
          'settings.recorder.descriptions.autoRenameRecordings',
          'Automatically generate meaningful names for recordings using AI based on transcript content',
        ),
      };
      return (
        toggleDescriptions[setting] ||
        t('settings.recorder.descriptions.defaultToggle', {
          setting: t(
            `settings.recorder.subSettingsTitle.${setting}`,
            formatSettingName(setting),
          ),
          defaultValue: `Enable ${formatSettingName(setting)}`,
        })
      );
    }

    const inputDescriptions: { [key: string]: string } = {
      webcamDeviceId: t(
        'settings.recorder.descriptions.webcamDeviceId',
        'Select which camera to use for webcam recording',
      ),
      microphoneDeviceId: t(
        'settings.recorder.descriptions.microphoneDeviceId',
        'Select which microphone to use for audio recording',
      ),
      resolution: t(
        'settings.recorder.descriptions.resolution',
        'Choose the quality and dimensions of your recording',
      ),
      frameRate: t(
        'settings.recorder.descriptions.frameRate',
        'Set how many frames per second to capture',
      ),
      countdownTimer: t(
        'settings.recorder.descriptions.countdownTimer',
        'How many seconds to wait before starting',
      ),
      defaultRecordingFormat: t(
        'settings.recorder.descriptions.defaultRecordingFormat',
        'Default video format for new recordings',
      ),
      formatProfile: t(
        'settings.recorder.descriptions.formatProfile',
        'Media format profile for transcoding and export',
      ),
      outputDirectory: t(
        'settings.recorder.descriptions.outputDirectory',
        'Where to save your recorded files',
      ),
      hotkeys: t(
        'settings.recorder.descriptions.hotkeys',
        'Customize your keyboard shortcuts',
      ),
      maxRecordingTimeMins: t(
        'settings.recorder.descriptions.maxRecordingTimeMins',
        'Maximum recording duration in minutes (0 for unlimited)',
      ),
      bufferSize: t(
        'settings.recorder.descriptions.bufferSize',
        'Adjust recording buffer size',
      ),
      audioQuality: t(
        'settings.recorder.descriptions.audioQuality',
        'Set the audio recording quality',
      ),
      videoQuality: t(
        'settings.recorder.descriptions.videoQuality',
        'Choose video recording quality',
      ),
    };
    return (
      inputDescriptions[setting] ||
      t('settings.recorder.descriptions.defaultInput', {
        setting: t(
          `settings.recorder.subSettingsTitle.${setting}`,
          formatSettingName(setting),
        ),
        defaultValue: `Choose ${formatSettingName(setting)}`,
      })
    );
  };

  const renderTextField = (
    setting: string,
    value: string,
    onChange: (newValue: string) => void,
  ) => {
    // Special handling for specific settings
    if (setting === 'defaultRecordingFormat' || setting === 'formatProfile') {
      const options =
        setting === 'defaultRecordingFormat'
          ? videoFormatOptions
          : formatOptions;
      const currentFormat = value || options[0];
      const formatDescriptions: Record<string, string> = {
        webm: 'Web video format',
        mp4: 'Universal video format',
        mkv: 'High quality video',
        mp3: 'Audio only',
        wav: 'Uncompressed audio',
        mp4a: 'AAC audio format',
      };

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="minimal" className="w-fit">
              <span>{currentFormat.toUpperCase()}</span>
              <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="min-w-[140px] max-w-[200px]"
          >
            {options.map((format) => {
              const isSelected = format === value;
              return (
                <DropdownMenuItem key={format} onClick={() => onChange(format)}>
                  <div className="flex items-center justify-between w-full">
                    <div className="flex flex-col">
                      <span className="font-medium">
                        {format.toUpperCase()}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDescriptions[format]}
                      </span>
                    </div>
                    {isSelected && (
                      <div className="w-2 h-2 bg-primary rounded-full ml-2" />
                    )}
                  </div>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    if (setting === 'maxRecordingTimeMins') {
      const numValue = parseInt(value, 10) || 0;
      const selectedOption =
        timeOptions.find((opt) => opt.value === numValue) || timeOptions[0];

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="minimal" className="w-fit">
              <span>{selectedOption.label}</span>
              <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="min-w-[140px] max-w-[160px]"
          >
            {timeOptions.map((option) => {
              const isSelected = option.value === numValue;
              return (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => onChange(option.value.toString())}
                >
                  <span className="flex-1">{option.label}</span>
                  {isSelected && (
                    <div className="w-2 h-2 bg-primary rounded-full ml-2" />
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    if (setting === 'countdownTimer') {
      const countdownOptions = [
        { value: 0, label: 'No delay' },
        { value: 1, label: '1 second' },
        { value: 2, label: '2 seconds' },
        { value: 3, label: '3 seconds' },
        { value: 5, label: '5 seconds' },
        { value: 10, label: '10 seconds' },
      ];
      const numValue = parseInt(value, 10) || 3;
      const selectedOption =
        countdownOptions.find((opt) => opt.value === numValue) ||
        countdownOptions[3];

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="minimal" className="w-fit">
              <span>{selectedOption.label}</span>
              <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="min-w-[140px] max-w-[160px]"
          >
            {countdownOptions.map((option) => {
              const isSelected = option.value === numValue;
              return (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => onChange(option.value.toString())}
                >
                  <span className="flex-1">{option.label}</span>
                  {isSelected && (
                    <div className="w-2 h-2 bg-primary rounded-full ml-2" />
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    if (setting === 'frameRate') {
      const frameRates = [15, 24, 25, 30, 48, 50, 60];
      const currentRate = parseInt(value, 10) || 30;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="minimal" className="w-fit">
              <span>{currentRate} fps</span>
              <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="min-w-[100px] max-w-[140px]"
          >
            {frameRates.map((rate) => {
              const isSelected = rate === currentRate;
              return (
                <DropdownMenuItem
                  key={rate}
                  onClick={() => onChange(rate.toString())}
                >
                  <span className="flex-1">{rate} fps</span>
                  {isSelected && (
                    <div className="w-2 h-2 bg-primary rounded-full ml-2" />
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    if (setting === 'resolution') {
      const resolutions = [
        { value: '720p', label: '720p HD' },
        { value: '1080p', label: '1080p Full HD' },
        { value: '1440p', label: '1440p 2K' },
        { value: '2160p', label: '2160p 4K' },
        { value: 'auto', label: 'Auto (Native)' },
      ];
      const currentValue = value || 'auto';
      const selectedRes =
        resolutions.find((r) => r.value === currentValue) || resolutions[4];

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="minimal" className="w-fit">
              <span>{selectedRes.label}</span>
              <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="min-w-[140px] max-w-[180px]"
          >
            {resolutions.map((res) => {
              const isSelected = res.value === currentValue;
              return (
                <DropdownMenuItem
                  key={res.value}
                  onClick={() => onChange(res.value)}
                >
                  <span className="flex-1">{res.label}</span>
                  {isSelected && (
                    <div className="w-2 h-2 bg-primary rounded-full ml-2" />
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    if (setting === 'audioQuality' || setting === 'videoQuality') {
      const qualities = [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
        { value: 'ultra', label: 'Ultra' },
      ];
      const currentValue = value || 'high';
      const selectedQuality =
        qualities.find((q) => q.value === currentValue) || qualities[2];

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="minimal" className="w-fit">
              <span>{selectedQuality.label}</span>
              <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="min-w-[100px] max-w-[140px]"
          >
            {qualities.map((quality) => {
              const isSelected = quality.value === currentValue;
              return (
                <DropdownMenuItem
                  key={quality.value}
                  onClick={() => onChange(quality.value)}
                >
                  <span className="flex-1">{quality.label}</span>
                  {isSelected && (
                    <div className="w-2 h-2 bg-primary rounded-full ml-2" />
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    // Default input for other settings
    return (
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full max-w-[200px]"
      />
    );
  };

  return (
    <SettingsLayout>
      {recordingSubSettings.map((subSetting, index) => (
        <React.Fragment key={subSetting}>
          <div className="space-y-4">
            <h3 className="font-medium text-base">
              {t(
                `settings.recorder.settingsTitle.${subSetting}`,
                formatString(subSetting),
              )}
            </h3>

            {Object.entries(modifiedSettings[subSetting] || {})
              .filter(([setting]) => {
                // Skip settings that are handled elsewhere in Account.tsx
                return (
                  setting !== 'defaultTranscriptionLanguage' &&
                  setting !== 'emailAlerts' &&
                  setting !== 'formatProfile' &&
                  setting !== 'outputLanguage'
                );
              })
              .map(([setting, value]) => {
                const isToggle = typeof value === 'boolean';
                return (
                  <SettingsSection
                    key={setting}
                    label={t(
                      `settings.recorder.subSettingsTitle.${setting}`,
                      formatString(setting),
                    )}
                    description={getSettingDescription(setting, isToggle)}
                    showSeparator={false}
                  >
                    {isToggle ? (
                      <Switch
                        checked={value as boolean}
                        onCheckedChange={(checked: boolean) =>
                          handleChange(subSetting, setting, checked)
                        }
                      />
                    ) : (
                      renderTextField(
                        setting,
                        value as string,
                        (newValue: string) =>
                          handleChange(subSetting, setting, newValue),
                      )
                    )}
                  </SettingsSection>
                );
              })}
          </div>

          {index < recordingSubSettings.length - 1 && <Separator />}
        </React.Fragment>
      ))}
    </SettingsLayout>
  );
};

export default RecordingSettings;
